import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function AnimBar({ pct, color }: { pct: number; color: string }) {
  const [w, setW] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setTimeout(() => setW(pct), 200);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [pct]);
  return (
    <div ref={ref} className="h-2 rounded-full bg-white/10 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-1000 ease-out`} style={{ width: `${w}%` }} />
    </div>
  );
}

function DualBarColumn({ heightA, heightB, maxVal }: { heightA: number; heightB: number; maxVal: number }) {
  const [hA, setHA] = useState(0);
  const [hB, setHB] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const MAX_H = 96;
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setTimeout(() => {
        setHA(Math.round((heightA / maxVal) * MAX_H));
        setHB(Math.round((heightB / maxVal) * MAX_H));
      }, 300);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [heightA, heightB, maxVal]);
  return (
    <div ref={ref} className="w-full flex items-end gap-0.5" style={{ height: MAX_H }}>
      <div className="flex-1 rounded-t-sm bg-gradient-to-t from-violet-600 to-violet-400 transition-all duration-1000 ease-out" style={{ height: `${hA}px` }} />
      <div className="flex-1 rounded-t-sm bg-gradient-to-t from-emerald-600 to-emerald-400 transition-all duration-1000 ease-out" style={{ height: `${hB}px` }} />
    </div>
  );
}

export default function CrossPlatformArbitrage() {
  const { t } = useTranslation();
  const [matchStep, setMatchStep] = useState(0);
  const [pulse, setPulse] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let s = 0;
      const iv = setInterval(() => {
        s++;
        setMatchStep(s);
        if (s >= 4) { clearInterval(iv); setPulse(true); }
      }, 700);
    }, { threshold: 0.2 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const markets = [
    { left: 'Will BTC end 2025 above $120K?', leftYes: 71, leftNo: 29, rightYes: 68, rightNo: 32, right: 'Bitcoin ends 2025 above $120,000?', spread: '+3.0%', profit: '$430' },
    { left: 'Will Fed cut rates in Q3 2025?', leftYes: 58, leftNo: 42, rightYes: 54, rightNo: 46, right: 'Fed rate cut by September 2025?', spread: '+4.0%', profit: '$580' },
  ];

  const apiNodes = [
    { label: 'Polymarket API', icon: 'ri-global-line', color: 'from-violet-500 to-purple-600' },
    { label: 'Kalshi API', icon: 'ri-bank-line', color: 'from-emerald-500 to-teal-600' },
    { label: 'Risk Engine', icon: 'ri-shield-check-line', color: 'from-blue-500 to-cyan-600' },
    { label: 'USDC Bridge', icon: 'ri-exchange-dollar-line', color: 'from-amber-500 to-orange-500' },
  ];

  const chartBars = [
    { label: 'Jan', poly: 42, kal: 31 }, { label: 'Feb', poly: 58, kal: 44 },
    { label: 'Mar', poly: 51, kal: 39 }, { label: 'Apr', poly: 74, kal: 56 },
    { label: 'May', poly: 67, kal: 52 }, { label: 'Jun', poly: 89, kal: 68 },
  ];

  return (
    <section ref={sectionRef} id="cross-platform-arbitrage" className="relative py-16 md:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d0826 40%, #060d1f 100%)' }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }} />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #2563eb 0%, transparent 70%)' }} />
      </div>
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        {/* Hero Banner */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 border border-purple-500/40 bg-purple-500/10 text-purple-300 text-sm font-medium tracking-wider uppercase">
            <i className="ri-broadcast-line animate-pulse"></i>
            {t('crossPlatform.badge')}
          </div>
          <h2 className="text-3xl md:text-6xl font-extrabold text-white mb-5 leading-tight tracking-tight px-2">
            <span style={{ background: 'linear-gradient(90deg,#a855f7,#60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('crossPlatform.title1')}
            </span>
            <br />
            <span className="text-white/90 text-2xl md:text-5xl">{t('crossPlatform.title2')}</span>
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed px-4">
            {t('crossPlatform.desc')}
          </p>
          <div className="mt-10 md:mt-14 flex items-center justify-center gap-0 px-4">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl flex flex-col items-center justify-center border border-violet-500/40 bg-violet-900/30">
                <i className="ri-global-line text-2xl md:text-3xl text-violet-400 mb-1"></i>
                <span className="text-xs text-violet-300 font-bold">{t('crossPlatform.polymarket')}</span>
                <span className="text-xs text-violet-500">{t('crossPlatform.web3')}</span>
              </div>
            </div>
            <div className="relative flex items-center flex-1 min-w-0 mx-1" style={{ maxWidth: 240, minWidth: 60 }}>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full h-0.5" style={{ background: 'linear-gradient(90deg, #7c3aed, #2563eb)' }} />
              </div>
              {[0, 1, 2].map(i => (
                <div key={i} className="absolute w-2 h-2 rounded-full bg-purple-400" style={{ animation: 'flowDot 2s linear infinite', animationDelay: `${i * 0.65}s` }} />
              ))}
              <div className="absolute left-1/2 -translate-x-1/2 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center z-10 border-2 border-purple-500/60"
                style={{ background: 'linear-gradient(135deg,#4f1d96,#1e3a8a)', boxShadow: '0 0 30px rgba(139,92,246,0.5)' }}>
                <span className="text-xl md:text-2xl select-none" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.4))' }}>🦞</span>
              </div>
              <span className="absolute left-1/2 -translate-x-1/2 top-9 md:top-10 text-xs text-purple-300 font-bold whitespace-nowrap">{t('crossPlatform.claw')}</span>
            </div>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl flex flex-col items-center justify-center border border-emerald-500/40 bg-emerald-900/30">
                <i className="ri-bank-line text-2xl md:text-3xl text-emerald-400 mb-1"></i>
                <span className="text-xs text-emerald-300 font-bold">{t('crossPlatform.kalshi')}</span>
                <span className="text-xs text-emerald-600">{t('crossPlatform.fiat')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-bold tracking-widest text-purple-400 uppercase border border-purple-500/30 px-3 py-1 rounded-full bg-purple-500/10">{t('crossPlatform.sec1_badge')}</span>
            <span className="text-xs text-white/40 tracking-wider uppercase">{t('crossPlatform.sec1_accuracy')}</span>
          </div>
          <h3 className="text-2xl md:text-4xl font-bold text-white mb-3">{t('crossPlatform.sec1_title')}</h3>
          <p className="text-white/50 mb-8 md:mb-10 max-w-xl text-sm md:text-base">{t('crossPlatform.sec1_desc')}</p>
          <div className="space-y-4 md:space-y-5">
            {markets.map((m, idx) => (
              <div key={idx} className="rounded-2xl p-4 md:p-5 border border-white/10 bg-white/[0.04]">
                {/* Mobile Layout */}
                <div className="md:hidden flex flex-col gap-3">
                  {/* Polymarket */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-global-line text-violet-400 text-sm"></i>
                      <span className="text-xs text-violet-400 font-semibold">{t('crossPlatform.polymarket')}</span>
                    </div>
                    <div className="rounded-xl p-3 border border-violet-500/20 bg-violet-900/20">
                      <p className="text-sm text-white/80 font-medium mb-2">{m.left}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-violet-600/30 border border-violet-500/40 px-2 py-1 rounded-md">
                          <span className="text-xs font-bold text-violet-300">YES</span>
                          <span className="text-xs font-bold text-white">{m.leftYes}%</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-md">
                          <span className="text-xs text-white/50">NO</span>
                          <span className="text-xs text-white/70">{m.leftNo}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Match Status */}
                  <div className="flex items-center justify-center gap-3 py-1">
                    <div className="flex-1 h-px" style={{ background: matchStep > idx * 2 ? 'linear-gradient(90deg,#7c3aed,#2563eb)' : 'rgba(255,255,255,0.1)', transition: 'background 0.5s' }} />
                    <div className="flex flex-col items-center gap-1">
                      {matchStep > idx * 2 ? (
                        <span className="text-xs font-bold text-emerald-400 animate-pulse">{t('crossPlatform.matched')}</span>
                      ) : (
                        <span className="text-xs text-white/30">{t('crossPlatform.matching')}</span>
                      )}
                      <div className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30">
                        <span className="text-xs font-bold text-emerald-400">+{m.spread} spread</span>
                      </div>
                      <div className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30">
                        <span className="text-xs font-bold text-amber-400">≈{m.profit}</span>
                      </div>
                    </div>
                    <div className="flex-1 h-px" style={{ background: matchStep > idx * 2 ? 'linear-gradient(90deg,#2563eb,#7c3aed)' : 'rgba(255,255,255,0.1)', transition: 'background 0.5s' }} />
                  </div>
                  {/* Kalshi */}
                  <div>
                    <div className="flex items-center gap-2 mb-2 justify-end">
                      <span className="text-xs text-emerald-400 font-semibold">{t('crossPlatform.kalshi')}</span>
                      <i className="ri-bank-line text-emerald-400 text-sm"></i>
                    </div>
                    <div className="rounded-xl p-3 border border-emerald-500/20 bg-emerald-900/20">
                      <p className="text-sm text-white/80 font-medium mb-2">{m.right}</p>
                      <div className="flex items-center gap-3 justify-end">
                        <div className="flex items-center gap-1 bg-emerald-600/30 border border-emerald-500/40 px-2 py-1 rounded-md">
                          <span className="text-xs font-bold text-emerald-300">YES</span>
                          <span className="text-xs font-bold text-white">{m.rightYes}%</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-md">
                          <span className="text-xs text-white/50">NO</span>
                          <span className="text-xs text-white/70">{m.rightNo}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Desktop Layout */}
                <div className="hidden md:grid grid-cols-11 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="flex items-center gap-2 mb-3"><i className="ri-global-line text-violet-400 text-sm"></i><span className="text-xs text-violet-400 font-semibold">{t('crossPlatform.polymarket')}</span></div>
                    <div className="rounded-xl p-3 border border-violet-500/20 bg-violet-900/20">
                      <p className="text-sm text-white/80 font-medium mb-2">{m.left}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 bg-violet-600/30 border border-violet-500/40 px-2 py-1 rounded-md"><span className="text-xs font-bold text-violet-300">YES</span><span className="text-xs font-bold text-white">{m.leftYes}%</span></div>
                        <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-md"><span className="text-xs text-white/50">NO</span><span className="text-xs text-white/70">{m.leftNo}%</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3 flex flex-col items-center gap-2">
                    <div className="w-full h-0.5" style={{ background: matchStep > idx * 2 ? 'linear-gradient(90deg,#7c3aed,#2563eb)' : 'rgba(255,255,255,0.1)', transition: 'background 0.5s' }}></div>
                    <div className="flex flex-col items-center">
                      {matchStep > idx * 2 ? <span className="text-xs font-bold text-emerald-400 animate-pulse">{t('crossPlatform.matched')}</span> : <span className="text-xs text-white/30">{t('crossPlatform.matching')}</span>}
                      <div className="mt-2 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30"><span className="text-xs font-bold text-emerald-400">+{m.spread} spread</span></div>
                      <div className="mt-1 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/30"><span className="text-xs font-bold text-amber-400">≈{m.profit}</span></div>
                    </div>
                  </div>
                  <div className="col-span-4">
                    <div className="flex items-center gap-2 mb-3 justify-end"><span className="text-xs text-emerald-400 font-semibold">{t('crossPlatform.kalshi')}</span><i className="ri-bank-line text-emerald-400 text-sm"></i></div>
                    <div className="rounded-xl p-3 border border-emerald-500/20 bg-emerald-900/20">
                      <p className="text-sm text-white/80 font-medium mb-2">{m.right}</p>
                      <div className="flex items-center gap-3 justify-end">
                        <div className="flex items-center gap-1 bg-emerald-600/30 border border-emerald-500/40 px-2 py-1 rounded-md"><span className="text-xs font-bold text-emerald-300">YES</span><span className="text-xs font-bold text-white">{m.rightYes}%</span></div>
                        <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-md"><span className="text-xs text-white/50">NO</span><span className="text-xs text-white/70">{m.rightNo}%</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 2 */}
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-bold tracking-widest text-blue-400 uppercase border border-blue-500/30 px-3 py-1 rounded-full bg-blue-500/10">{t('crossPlatform.sec2_badge')}</span>
            <span className="text-xs text-white/40 tracking-wider uppercase">{t('crossPlatform.sec2_ms')}</span>
          </div>
          <h3 className="text-2xl md:text-4xl font-bold text-white mb-3">{t('crossPlatform.sec2_title')}</h3>
          <p className="text-white/50 mb-8 md:mb-10 max-w-xl text-sm md:text-base">{t('crossPlatform.sec2_desc')}</p>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-8">
            <div className="flex flex-col items-center gap-6 md:gap-10 lg:flex-row">
              <div className="flex-shrink-0">
                <div className="w-28 h-28 md:w-40 md:h-40 rounded-3xl flex flex-col items-center justify-center relative"
                  style={{ background: 'linear-gradient(135deg,#4f1d96,#1e3a8a)', boxShadow: `0 0 ${pulse ? '50px' : '20px'} rgba(139,92,246,0.4)`, transition: 'box-shadow 0.5s' }}>
                  <span className="text-4xl md:text-5xl mb-1 md:mb-2 select-none">🦞</span>
                  <span className="text-white font-extrabold text-base md:text-lg">CLaw</span>
                  <span className="text-purple-300 text-xs">Core Engine</span>
                  <div className="absolute inset-0 rounded-3xl border border-purple-400/30 animate-ping" style={{ animationDuration: '2s' }} />
                </div>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 w-full">
                {apiNodes.map((node, idx) => (
                  <div key={idx} className="rounded-xl p-3 md:p-4 border border-white/10 bg-white/[0.04] flex items-center gap-3 hover:border-purple-500/40 transition-colors cursor-default">
                    <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br ${node.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${node.icon} text-white text-base md:text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{node.label}</div>
                      <div className="text-xs text-white/40">{t('crossPlatform.connected')}</div>
                    </div>
                    <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" style={{ boxShadow: '0 0 8px #34d399' }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[
                { labelKey: 'crossPlatform.latency', value: '<12ms', icon: 'ri-time-line', color: 'text-purple-400' },
                { labelKey: 'crossPlatform.dailyEvents', value: '300+', icon: 'ri-links-line', color: 'text-blue-400' },
                { labelKey: 'crossPlatform.successRate', value: '99.7%', icon: 'ri-checkbox-circle-line', color: 'text-emerald-400' },
                { labelKey: 'crossPlatform.platforms', value: '2', icon: 'ri-server-line', color: 'text-amber-400' },
              ].map((m, i) => (
                <div key={i} className="rounded-xl p-3 md:p-4 border border-white/8 bg-white/[0.03] text-center">
                  <i className={`${m.icon} text-xl md:text-2xl ${m.color} mb-1 md:mb-2 block`}></i>
                  <div className="text-xl md:text-2xl font-bold text-white">{m.value}</div>
                  <div className="text-xs text-white/40 mt-1">{t(m.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 3 */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-bold tracking-widest text-emerald-400 uppercase border border-emerald-500/30 px-3 py-1 rounded-full bg-emerald-500/10">{t('crossPlatform.sec3_badge')}</span>
            <span className="text-xs text-white/40 tracking-wider uppercase">{t('crossPlatform.sec3_analytics')}</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">{t('crossPlatform.sec3_title')}</h3>
          <p className="text-white/50 mb-10 max-w-xl">{t('crossPlatform.sec3_desc')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="rounded-2xl p-6 border border-white/10 bg-white/[0.04]">
              <div className="flex items-center gap-2 mb-5"><i className="ri-pulse-line text-purple-400 text-xl"></i><span className="text-sm font-semibold text-white">{t('crossPlatform.priceDeviation')}</span></div>
              <div className="space-y-4">
                {[{ event: 'BTC Annual', poly: 71, kal: 68, diff: 3 }, { event: 'Rate Cut Q3', poly: 58, kal: 54, diff: 4 }, { event: 'Election', poly: 62, kal: 57, diff: 5 }, { event: 'CPI Data', poly: 45, kal: 42, diff: 3 }].map((d, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs text-white/50 mb-1"><span>{d.event}</span><span className="text-emerald-400 font-bold">+{d.diff}%</span></div>
                    <div className="flex gap-1 items-center">
                      <div className="flex-1"><AnimBar pct={d.poly} color="bg-violet-500" /><span className="text-xs text-violet-400">{d.poly}%</span></div>
                      <div className="text-white/20 text-xs">|</div>
                      <div className="flex-1"><AnimBar pct={d.kal} color="bg-emerald-500" /><span className="text-xs text-emerald-400">{d.kal}%</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl p-6 border border-white/10 bg-white/[0.04]">
              <div className="flex items-center justify-between mb-5"><div className="flex items-center gap-2"><i className="ri-bar-chart-grouped-line text-blue-400 text-xl"></i><span className="text-sm font-semibold text-white">{t('crossPlatform.volumeTitle')}</span></div></div>
              <div className="flex items-end gap-1.5 h-36 mt-2">
                {chartBars.map((b, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end gap-0.5" style={{ height: 100 }}>
                      <DualBarColumn heightA={b.poly} heightB={b.kal} maxVal={89} />
                    </div>
                    <span className="text-xs text-white/40">{b.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-3 text-xs text-white/50">
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-violet-500" />{t('crossPlatform.polymarket')}</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" />{t('crossPlatform.kalshi')}</span>
              </div>
              <div className="mt-4 rounded-xl p-3 bg-white/[0.04] border border-white/8 text-center">
                <div className="text-xl font-bold text-white">614K</div>
                <div className="text-xs text-white/40">{t('crossPlatform.h1Volume')}</div>
              </div>
            </div>
            <div className="rounded-2xl p-6 border border-white/10 bg-white/[0.04]">
              <div className="flex items-center gap-2 mb-5"><i className="ri-line-chart-line text-emerald-400 text-xl"></i><span className="text-sm font-semibold text-white">{t('crossPlatform.arbIndicators')}</span></div>
              <div className="space-y-5">
                {[
                  { labelKey: 'crossPlatform.arbSuccess', val: 94, color: 'from-purple-500 to-violet-400' },
                  { labelKey: 'crossPlatform.semanticAccuracy', val: 99, color: 'from-blue-500 to-cyan-400' },
                  { labelKey: 'crossPlatform.liquidity', val: 87, color: 'from-emerald-500 to-teal-400' },
                  { labelKey: 'crossPlatform.hedgeCoverage', val: 96, color: 'from-amber-500 to-orange-400' },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-white/60">{t(item.labelKey)}</span>
                      <span className="font-bold text-white">{item.val}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <AnimBar pct={item.val} color={`bg-gradient-to-r ${item.color}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes flowDot { 0% { left: 0%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { left: 100%; opacity: 0; } }`}</style>
    </section>
  );
}
